/**
 * Watchlist helpers for the consumer portal (cross-portal port of Issue #1424).
 *
 * Consumers only pin entities they own — `case`, `loan`, `application`. The
 * other HomePinEntityType values (`partner`, `contact`, `consumer`) are not
 * surfaced in consumer UI; the lookup tables here cover them with sensible
 * fallbacks so a stale pin from another portal never crashes a render.
 *
 * Re-exports the wire types so components don't have to know they live in
 * shared-types.
 */
import { baseColors } from '@ops-matrix/design-system-ui';
import type { HomePinEntityType } from '@ops-matrix/shared-types';

export type { HomePin, HomePinEntityType } from '@ops-matrix/shared-types';

/**
 * Subset of pin types the consumer portal natively pins from its own UI.
 * Used by the picker dialog to scope what we offer in the search results.
 *
 * `'rate'` is added for the rate-watch surface — the consumer pins a rate
 * threshold and 27-notification-service fires when crossed. The
 * notification-trigger backend ships as a follow-up; the pin type itself
 * is wired here so the row renders correctly when a rate pin lands.
 */
export type ConsumerPinKind = Extract<HomePinEntityType, 'case' | 'loan' | 'application' | 'rate'>;

/**
 * Where each entity-type's pinned-item link goes inside the consumer portal.
 * Consumer routes are scoped to the user's own data via auth context.
 *
 * Fallbacks for partner/contact/consumer return `/dashboard` so a stale pin
 * from another portal still resolves to a safe page rather than a 404.
 */
export const ENTITY_DEEP_LINK: Record<HomePinEntityType, (entityId: string) => string> = {
  case: (id) => `/applications/${id}`,
  loan: (id) => `/applications/${id}`,
  application: (id) => `/applications/${id}`,
  partner: () => '/dashboard',
  contact: () => '/dashboard',
  consumer: () => '/dashboard',
  // Rate-watch: route to the watchlist page itself (no per-rate detail surface
  // yet — the notification-trigger backend that would drive a detail view is
  // a follow-up). The watchlist row shows the threshold inline so deep-link
  // back to /watchlist still feels useful.
  rate: () => '/watchlist',
};

/**
 * Border + text color for the entity-type chip. Drives the outlined-pill
 * style that mirrors `<ClassificationChip>` on PrioritySignals.
 */
export const ENTITY_ACCENT: Record<HomePinEntityType, string> = {
  case: baseColors.info[400],
  loan: baseColors.teal[400],
  application: baseColors.secondary[400],
  partner: baseColors.success[500],
  contact: baseColors.primary[400],
  consumer: baseColors.warning[500],
  rate: baseColors.amber[400],
};

/**
 * Low-alpha background fill (15% via `26` hex suffix). Pairs with
 * `ENTITY_ACCENT` to render the outlined-pill chip.
 */
export const ENTITY_BG: Record<HomePinEntityType, string> = {
  case: `${baseColors.info[400]}26`,
  loan: `${baseColors.teal[400]}26`,
  application: `${baseColors.secondary[400]}26`,
  partner: `${baseColors.success[500]}26`,
  contact: `${baseColors.primary[400]}26`,
  consumer: `${baseColors.warning[500]}26`,
  rate: `${baseColors.amber[400]}26`,
};
